import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { TDocumentDefinitions } from "pdfmake/interfaces";
import logo from "../../asset/KALAMATA_LOGO.png";

(pdfMake.vfs as any) = pdfFonts.vfs;

/* =====================================================
 * TYPES
 * ===================================================== */
interface StoreInvoiceItem {
  category?: string | null;
  sub_category?: string | null;
  product?: string | null;
  qty_packed?: number | string | null;
  qty_unpacked?: number | string | null;
  initialQty?: number | string | null;
  packed?: number | string | null;
  unit?: string | null;
  cost?: number | string | null;
  msrp?: number | string | null;
  invoice_no?: string | number | null;
  store?: string | null;
  first_name?: string | null;
}
type GroupedPayload = Record<string, Record<string, StoreInvoiceItem[]>>;

/* =====================================================
 * HELPERS
 * ===================================================== */
const toNumber = (value: number | string | null | undefined): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Formats displayed quantities/numbers to a maximum of 2 decimal places.
 * This is display-only and does not change calculation logic.
 */
const formatNumber = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    return "";
  }

  return parsed.toLocaleString("en-CA", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const formatMoney = (value: number): string =>
  `$${value.toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * Returns the price for one individual unit.
 *
 * Example:
 * packed = 12
 * unit = KG
 * packPrice = $60
 *
 * pricePerUnit = $5 / KG
 */
const calculatePricePerUnit = (packPrice: number, packed: number): number => {
  if (packed <= 0) {
    return packPrice;
  }
  return packPrice / packed;
};

/**
 * Creates the price display used in Cost / MSRP columns.
 *
 * Example:
 *
 * $300.00
 * $60.00 / pack
 * $5.00 / KG
 */
const createPriceStack = (
  lineTotal: number,
  packPrice: number,
  packed: number,
  unit?: string | null,
) => {
  const normalizedUnit = unit?.trim().toUpperCase();
  const pricePerUnit = calculatePricePerUnit(packPrice, packed);
  const stack: any[] = [
    {
      text: formatMoney(lineTotal),
      style: "priceTotal",
    },
    {
      text: `${formatMoney(packPrice)} / pack`,
      style: "priceDetail",
    },
  ];

  /*
   * Only show per-unit pricing when we actually
   * know what the unit is.
   */
  if (normalizedUnit) {
    stack.push({
      text: `${formatMoney(pricePerUnit)} / ${normalizedUnit}`,
      style: "pricePerUnit",
    });
  }

  return stack;
};

/* =====================================================
 * IMAGE CONVERTER
 * ===================================================== */
const convertImageToBase64 = async (url: string): Promise<string> => {
  const response = await fetch(url);
  const blob = await response.blob();

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/* =====================================================
 * GROUPING
 * ===================================================== */
const groupPayload = (data: StoreInvoiceItem[]): GroupedPayload => {
  const grouped: GroupedPayload = {};

  data.forEach((item) => {
    const category = item.category || "Other";

    const subCategory = item.sub_category || "Other";

    if (!grouped[category]) {
      grouped[category] = {};
    }

    if (!grouped[category][subCategory]) {
      grouped[category][subCategory] = [];
    }

    grouped[category][subCategory].push(item);
  });
  return grouped;
};

/* =====================================================
 * MAIN FUNCTION
 * ===================================================== */
export const storeInvoice = async (
  payload: StoreInvoiceItem[],
  requestedBy: string,
  notes?: string,
  discount?: number,
  showCost = false,
) => {
  if (payload.length === 0) {
    return;
  }

  const imageDataUrl = await convertImageToBase64(logo);
  const groupedData = groupPayload(payload);

  /* =====================================================
   * DISCOUNT
   * ===================================================== */
  const discountPercentage = Math.max(0, Math.min(toNumber(discount), 100));

  /* =====================================================
   * OVERALL PRICING CALCULATIONS
   * ===================================================== */

  /*
   * COST:
   * Qty Packed x Pack Cost
   */
  const totalCost = payload.reduce((total, item) => {
    const qtyPacked = toNumber(item.qty_packed);
    const packCost = toNumber(item.cost);

    return total + qtyPacked * packCost;
  }, 0);

  /*
   * MSRP:
   * Qty Packed x Pack MSRP
   */
  const totalMsrp = payload.reduce((total, item) => {
    const qtyPacked = toNumber(item.qty_packed);
    const packMsrp = toNumber(item.msrp);
    return total + qtyPacked * packMsrp;
  }, 0);

  /*
   * Discount is applied to MSRP.
   */
  const discountAmount = totalMsrp * (discountPercentage / 100);
  const msrpAfterDiscount = totalMsrp - discountAmount;

  /* =====================================================
   * TABLE HEADER
   * ===================================================== */
  const tableHeader: any[] = [
    {
      text: "Product",
      style: "tableHeader",
    },
    {
      text: "Qty Packed",
      style: "tableHeader",
      alignment: "right",
    },
    {
      text: "Qty Unpacked",
      style: "tableHeader",
      alignment: "right",
    },
    {
      text: "Initial",
      style: "tableHeader",
      alignment: "right",
    },
  ];

  /*
   * Pricing remains completely hidden when showCost=false.
   */
  if (showCost) {
    tableHeader.push(
      {
        text: "Cost",
        style: "tableHeader",
        alignment: "right",
      },
      {
        text: "MSRP",
        style: "tableHeader",
        alignment: "right",
      },
    );
  }

  const tableBody: any[] = [tableHeader];
  const columnCount = showCost ? 6 : 4;

  /* =====================================================
   * TABLE DATA
   * ===================================================== */
  Object.keys(groupedData).forEach((category) => {
    let categoryQtyTotal = 0;
    let categoryUnpackedQtyTotal = 0;
    let categoryCostTotal = 0;
    let categoryMsrpTotal = 0;

    /* ------------------ CATEGORY ------------------ */
    tableBody.push([
      {
        text: category.toUpperCase(),
        colSpan: columnCount,
        style: "categoryRow",
      },
      ...Array.from(
        {
          length: columnCount - 1,
        },
        () => ({}),
      ),
    ]);

    Object.keys(groupedData[category]).forEach((subCategory) => {
      /* ------------------ SUBCATEGORY ------------------ */
      tableBody.push([
        {
          text: subCategory,
          colSpan: columnCount,
          style: "subCategoryRow",
        },
        ...Array.from(
          {
            length: columnCount - 1,
          },
          () => ({}),
        ),
      ]);

      groupedData[category][subCategory].forEach((item) => {
        /* ------------------ QUANTITY ------------------ */

        const qtyPacked = toNumber(item.qty_packed);
        const qtyUnpacked = toNumber(item.qty_unpacked);
        const packed = toNumber(item.packed);

        /* ------------------ COST ------------------ */
        const packCost = toNumber(item.cost);
        const lineCost =
          qtyPacked * packCost + qtyUnpacked * (packCost / packed);
        /* ------------------ MSRP ------------------ */
        const packMsrp = toNumber(item.msrp);
        const lineMsrp = qtyPacked * packMsrp;

        /* ------------------ CATEGORY TOTALS ------------------ */
        categoryQtyTotal += qtyPacked;
        categoryUnpackedQtyTotal += qtyUnpacked;
        categoryCostTotal += lineCost;
        categoryMsrpTotal += lineMsrp;

        /* ------------------ PRODUCT NAME ------------------ */

        /*
         * Example:
         *
         * Kalamata Olives 12 KG
         */
        const productDescription = [
          `${item.product} (${formatNumber(item.packed)} ${item.unit})`,
        ]
          .filter(Boolean)
          .join(" ");

        const productRow: any[] = [
          {
            text: productDescription,
            style: "productRow",
          },

          {
            text: qtyPacked ? `${formatNumber(qtyPacked)} pack` : "",
            style: "numberCell",
            alignment: "right",
          },

          {
            text: qtyUnpacked
              ? `${formatNumber(qtyUnpacked)} ${item?.unit ?? ""}`
              : "",
            style: "numberCell",
            alignment: "right",
          },

          {
            text: formatNumber(item.initialQty),
            style: "numberCell",
            alignment: "right",
          },
        ];

        /* =====================================================
         * CONDITIONAL COST + MSRP
         * ===================================================== */
        if (showCost) {
          /*
           * COST
           *
           * Example:
           *
           * $300.00
           * $60.00 / pack
           * $5.00 / KG
           */
          productRow.push({
            stack: createPriceStack(lineCost, packCost, packed, item.unit),
            alignment: "right",
          });

          /*
           * MSRP
           *
           * Example:
           *
           * $450.00
           * $90.00 / pack
           * $7.50 / KG
           */
          productRow.push({
            stack: createPriceStack(lineMsrp, packMsrp, packed, item.unit),
            alignment: "right",
          });
        }
        tableBody.push(productRow);
      });
    });

    /* =====================================================
     * CATEGORY TOTAL
     * ===================================================== */
    const categoryTotalRow: any[] = [
      {
        text: `Total ${category}`,
        style: "categoryTotalLabel",
        alignment: "right",
      },
      {
        text: formatNumber(categoryQtyTotal),
        style: "categoryTotalValue",
        alignment: "right",
      },
      {
        text: formatNumber(categoryUnpackedQtyTotal),
        style: "categoryTotalValue",
        alignment: "right",
      },
      {},
    ];

    if (showCost) {
      categoryTotalRow.push(
        {
          text: formatMoney(categoryCostTotal),
          style: "categoryCostTotal",
          alignment: "right",
        },
        {
          text: formatMoney(categoryMsrpTotal),
          style: "categoryMsrpTotal",
          alignment: "right",
        },
      );
    }

    tableBody.push(categoryTotalRow);
  });

  /* =====================================================
   * PRICE SUMMARY
   * ===================================================== */
  const pricingSummaryBody: any[] = [
    [
      {
        text: "TOTAL COST",
        style: "costSummaryLabel",
      },
      {
        text: formatMoney(totalCost),
        style: "costSummaryValue",
      },
    ],
    [
      {
        text: "TOTAL MSRP",
        style: "costSummaryLabel",
      },
      {
        text: formatMoney(totalMsrp),
        style: "costSummaryValue",
      },
    ],
  ];

  /*
   * Only show the discount row when there is a discount.
   */
  if (discountPercentage > 0) {
    pricingSummaryBody.push([
      {
        text: `DISCOUNT (${formatNumber(discountPercentage)}%)`,
        style: "costSummaryLabel",
      },
      {
        text: `-${formatMoney(discountAmount)}`,
        style: "costSummaryDiscount",
      },
    ]);
  }

  /*
   * When discount exists, show MSRP after discount.
   */
  if (discountPercentage > 0) {
    pricingSummaryBody.push([
      {
        text: "MSRP AFTER DISCOUNT",
        style: "finalCostLabel",
        fillColor: "#e8f0dd",
      },
      {
        text: formatMoney(msrpAfterDiscount),
        style: "finalCostValue",
        fillColor: "#e8f0dd",
      },
    ]);
  }

  const pricingSummarySection: any[] = showCost
    ? [
        {
          columns: [
            {
              width: "*",
              text: "",
            },
            {
              width: 275,
              table: {
                widths: [165, "*"],
                body: pricingSummaryBody,
              },

              layout: {
                hLineWidth: () => 0.6,
                vLineWidth: () => 0.6,
                hLineColor: () => "#555555",
                vLineColor: () => "#555555",
                paddingLeft: () => 8,
                paddingRight: () => 8,
                paddingTop: () => 5,
                paddingBottom: () => 5,
              },
            },
          ],

          margin: [0, 0, 0, 25],
        },
      ]
    : [];

  /* =====================================================
   * DOCUMENT
   * ===================================================== */
  const docDefinition: TDocumentDefinitions = {
    pageSize: "LETTER",
    pageMargins: [40, 40, 40, 60],
    content: [
      /* =================================================
       * HEADER
       * ================================================= */
      {
        columns: [
          {
            image: imageDataUrl,
            fit: [80, 80],
          },
          {
            text: "Asadi Inc\n125 Martin Ross Avenue\nNorth York ON M3J 2L9\nPhone: (416) 665-3003\nEmail: info@kalamataltd.com",
            style: "companyInfo",
          },
          {
            width: "*",
            stack: [
              {
                text: "Store Order Summary",
                style: "invoiceTitle",
              },
              {
                text: `Invoice #: ${
                  payload[0]?.invoice_no || "N/A"
                }\nDate: ${new Date().toLocaleDateString()}\nLocation: ${
                  payload[0]?.store || ""
                }\nRequested By: ${requestedBy || ""}`,
                style: "invoiceMeta",
              },
            ],
            alignment: "right",
          },
        ],
      },

      /* ------------------ DIVIDER ------------------ */
      {
        canvas: [
          {
            type: "line",
            x1: 0,
            y1: 10,
            x2: 515,
            y2: 10,
            lineWidth: 1,
            lineColor: "#6b8e23",
          },
        ],
        margin: [0, 15, 0, 20],
      },
      /* ------------------ NOTES ------------------ */
      notes
        ? {
            text: `Notes: ${notes}`,
            style: "notes",
          }
        : {
            text: "",
          },

      /* =================================================
       * TABLE
       * ================================================= */
      {
        table: {
          /*
           * Normal:
           *
           * Product | Packed | Unpacked | Initial
           *
           * Pricing:
           *
           * Product | Packed | Unpacked | Initial | Cost | MSRP
           */
          widths: showCost ? ["*", 52, 62, 42, 88, 88] : ["*", 70, 90, 60],
          headerRows: 1,
          /*
           * Keep individual rows together.
           */
          dontBreakRows: true,
          body: tableBody,
        },

        layout: {
          hLineWidth: () => 0.3,
          vLineWidth: () => 0.3,
          hLineColor: () => "#d7d7d7",
          vLineColor: () => "#e0e0e0",
          /*
           * Using layout padding instead of changing
           * the existing visual structure significantly.
           */
          paddingLeft: () => 4,
          paddingRight: () => 4,
          paddingTop: () => 2,
          paddingBottom: () => 2,
        },
        margin: [0, 20, 0, showCost ? 15 : 30],
      },

      /* =================================================
       * CONDITIONAL PRICING SUMMARY
       * ================================================= */
      ...pricingSummarySection,

      /* =================================================
       * SIGNATURE SECTION
       * ================================================= */
      {
        columns: [
          {
            stack: [
              {
                text: "Storage Keeper",
                style: "signatureLabel",
              },
              {
                text: " ",
                margin: [0, 25, 0, 0],
              },
              {
                canvas: [
                  {
                    type: "line",
                    x1: 0,
                    y1: 0,
                    x2: 150,
                    y2: 0,
                    lineWidth: 1,
                  },
                ],
              },
            ],
          },
          {
            stack: [
              {
                text: "Driver Delivery",
                style: "signatureLabel",
              },
              {
                text: " ",
                margin: [0, 25, 0, 0],
              },
              {
                canvas: [
                  {
                    type: "line",
                    x1: 0,
                    y1: 0,
                    x2: 150,
                    y2: 0,
                    lineWidth: 1,
                  },
                ],
              },
            ],
            alignment: "center",
          },
          {
            stack: [
              {
                text: "Recipient",
                style: "signatureLabel",
              },
              {
                text: " ",
                margin: [0, 25, 0, 0],
              },
              {
                canvas: [
                  {
                    type: "line",
                    x1: 0,
                    y1: 0,
                    x2: 150,
                    y2: 0,
                    lineWidth: 1,
                  },
                ],
              },
            ],
            alignment: "right",
          },
        ],
      },
    ],

    /* =====================================================
     * STYLES
     * ===================================================== */
    styles: {
      invoiceTitle: {
        fontSize: 22,
        bold: true,
        color: "#6b8e23",
      },
      invoiceMeta: {
        fontSize: 10,
        lineHeight: 1.4,
      },
      companyInfo: {
        fontSize: 10,
        lineHeight: 1.4,
        color: "#555",
        margin: [-110, 0, 0, 0],
      },
      notes: {
        margin: [0, 10, 0, 0],
        fontSize: 9,
        italics: true,
        color: "#555",
      },

      /* ------------------ TABLE ------------------ */

      tableHeader: {
        fillColor: "#6b8e23",
        color: "white",
        bold: true,
        fontSize: 9,
        margin: [3, 5, 3, 5],
      },
      categoryRow: {
        fillColor: "#e8f0dd",
        bold: true,
        fontSize: 11,
        margin: [5, 6, 5, 6],
      },
      subCategoryRow: {
        fillColor: "#f4f7ef",
        bold: true,
        fontSize: 9,
        margin: [15, 5, 5, 5],
      },
      productRow: {
        fontSize: 9,
        margin: [20, 4, 3, 4],
      },
      numberCell: {
        fontSize: 9,
        margin: [0, 4, 0, 4],
      },

      /* ------------------ PRICE COLUMNS ------------------ */

      priceTotal: {
        fontSize: 8.5,
        bold: true,
        color: "#111111",
      },
      priceDetail: {
        fontSize: 6.5,
        color: "#666666",
        margin: [0, 2, 0, 0],
      },
      pricePerUnit: {
        fontSize: 6.5,
        bold: true,
        color: "#6b8e23",
        margin: [0, 1, 0, 0],
      },

      /* ------------------ CATEGORY TOTAL ------------------ */

      categoryTotalLabel: {
        bold: true,
        fontSize: 9,
        margin: [0, 6, 0, 6],
      },
      categoryTotalValue: {
        bold: true,
        fontSize: 9,
        color: "#6b8e23",
      },
      categoryCostTotal: {
        bold: true,
        fontSize: 8.5,
        color: "#6b8e23",
      },
      categoryMsrpTotal: {
        bold: true,
        fontSize: 8.5,
        color: "#6b8e23",
      },

      /* ------------------ PRICING SUMMARY ------------------ */

      costSummaryLabel: {
        fontSize: 8.5,
        bold: true,
        color: "#111111",
      },
      costSummaryValue: {
        fontSize: 8.5,
        bold: true,
        alignment: "right",
        color: "#111111",
      },
      costSummaryDiscount: {
        fontSize: 8.5,
        bold: true,
        alignment: "right",
        color: "#b42318",
      },
      finalCostLabel: {
        fontSize: 9,
        bold: true,
        color: "#111111",
      },
      finalCostValue: {
        fontSize: 10,
        bold: true,
        alignment: "right",
        color: "#6b8e23",
      },

      /* ------------------ SIGNATURE ------------------ */

      signatureLabel: {
        fontSize: 10,
        bold: true,
      },
    },
  };

  /* =====================================================
   * CREATE PDF
   * ===================================================== */
  pdfMake.createPdf(docDefinition).getBlob((blob) => {
    const blobUrl = URL.createObjectURL(blob);
    const newWindow = window.open(blobUrl, "_blank");

    if (!newWindow) {
      pdfMake.createPdf(docDefinition).download("invoice.pdf");
    }

    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
    }, 60000);
  });
};
